import { useState, useEffect, useCallback, useMemo } from 'react';
import AddProductModal, { type NewProductData } from '../modals/AddProductModal';
import { type Product } from '../../types';
import { isRebateParentCategory } from '../../../../../../../utils/vendorCategoryUtils';

interface Props {
  customerId: string;
  onSupplyAmountUpdate?: (amount: number) => void;
}

const toNumber = (value: unknown, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const pickFirstString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '-';
};

// 소분류 idx/name → 대분류 name 매핑
type CategoryMap = Map<number, string>; // childIdx → parentName
type CategoryNameMap = Map<string, string>; // childName → parentName

export default function ProductsTab({ customerId, onSupplyAmountUpdate }: Props) {
  const [products, setProducts] = useState<Product[]>([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [categoryIdxMap, setCategoryIdxMap] = useState<CategoryMap>(new Map());
  const [categoryNameMap, setCategoryNameMap] = useState<CategoryNameMap>(new Map());

  // vendorCategory API에서 대분류/소분류 매핑 구축
  useEffect(() => {
    const fetchCategoryTree = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch('https://api.wedmin.biz/vendor/category', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        const tree = Array.isArray(result) ? result : result?.status ? result.data : [];
        const idxMap: CategoryMap = new Map();
        const nameMap: CategoryNameMap = new Map();
        for (const parent of tree) {
          const parentName = parent.label || parent.name || '미지정';
          // 대분류 자체도 매핑
          idxMap.set(parent.vendorCategoryIdx, parentName);
          nameMap.set(parentName, parentName);
          for (const child of parent.children || []) {
            const childName = child.label || child.name;
            idxMap.set(child.vendorCategoryIdx, parentName);
            if (childName) nameMap.set(childName, parentName);
          }
        }
        setCategoryIdxMap(idxMap);
        setCategoryNameMap(nameMap);
      } catch (e) {
        console.error('Failed to fetch category tree:', e);
      }
    };
    fetchCategoryTree();
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=PRODUCTS`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status && result.data && Array.isArray(result.data.tabData)) {
        setProducts(result.data.tabData.map((apiProd: any) => ({
          id: String(apiProd.customerProductItemIdx),
          customerProductItemIdx: toNumber(apiProd.customerProductItemIdx),
          productIdx: apiProd.product?.productIdx || undefined,
          category: pickFirstString(apiProd.categoryName),
          productName: pickFirstString(apiProd.productName, apiProd.product?.name),
          vendor: pickFirstString(
            apiProd.vendor?.name,
            apiProd.vendorName,
            apiProd.partnerVendor?.name,
            apiProd.product?.vendorName
          ),
          vendorCategory: pickFirstString(
            apiProd.vendor?.category?.name,
            apiProd.vendorCategoryName,
            apiProd.vendor?.categoryName,
            apiProd.product?.category
          ),
          vendorParentCategory: pickFirstString(
            apiProd.vendor?.category?.parent?.name,
            apiProd.vendorParentCategoryName
          ),
          quantity: toNumber(apiProd.quantity),
          costPrice: toNumber(apiProd.costPrice),
          salesPrice: toNumber(apiProd.salesPrice),
          vat: toNumber(apiProd.vatAmount),
          orderStatus: pickFirstString(apiProd.status),
          memo: typeof apiProd.memo === 'string' ? apiProd.memo : '',
          // 혼수/리베이트 전용 필드
          contractDate: apiProd.contractDate || null,
          contractItems: apiProd.contractItems || null,
          deposit: toNumber(apiProd.deposit),
          rebateRate: apiProd.rebateRate != null ? Number(apiProd.rebateRate) : null,
          rebateAmount: toNumber(apiProd.rebateAmount),
          expectedPaymentDate: apiProd.expectedPaymentDate || null
        })));
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, [customerId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // 총공급액 계산 후 부모에 전달 (일반상품 costPrice + 리베이트 rebateAmount)
  useEffect(() => {
    if (!onSupplyAmountUpdate) return;
    const generalCost = products
      .filter((p) => {
        const pc = getParentCategory(p);
        return pc !== '혼수업체' && pc !== '웨딩홀' && pc !== '허니문';
      })
      .reduce((s, p) => s + p.costPrice * p.quantity, 0);
    const rebateCost = products
      .filter((p) => {
        const pc = getParentCategory(p);
        return pc === '혼수업체' || pc === '웨딩홀' || pc === '허니문';
      })
      .reduce((s, p) => s + ((p as any).rebateAmount || 0), 0);
    onSupplyAmountUpdate(generalCost + rebateCost);
  }, [products, categoryNameMap, onSupplyAmountUpdate]);

  const handleSaveCustomerProduct = async (data: NewProductData) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (editingProduct) {
        const itemIdx = editingProduct.id; 
        const response = await fetch(`https://api.wedmin.biz/customer/product/${itemIdx}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            customerIdx: Number(customerId),
            vendorIdx: data.vendorIdx,
            productIdx: data.productIdx || null,
            productName: data.productName,
            categoryName: data.categoryName,
            totalPrice: data.salesPrice,
            supplyPrice: data.supplyPrice,
            vat: data.vat,
            quantity: data.quantity,
            memo: data.memo,
            contractDate: data.contractDate || undefined,
            contractItems: data.contractItems || undefined,
            deposit: data.deposit || undefined,
            rebateRate: data.rebateRate || undefined,
            rebateAmount: data.rebateAmount || undefined,
            expectedPaymentDate: data.expectedPaymentDate || undefined,
          })
        });
        if (response.ok) {
          alert('상품 정보가 수정되었습니다.');
          setShowAddProductModal(false);
          setEditingProduct(null);
          fetchProducts();
        } else {
          const error = await response.json();
          alert(`수정 실패: ${error.message}`);
        }
      } else {
        const response = await fetch(`https://api.wedmin.biz/customer/${customerId}/product`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            customerIdx: Number(customerId),
            vendorIdx: data.vendorIdx,
            productIdx: data.productIdx || null,
            productName: data.productName,
            categoryName: data.categoryName,
            totalPrice: data.salesPrice,
            supplyPrice: data.supplyPrice,
            vat: data.vat,
            quantity: data.quantity,
            memo: data.memo,
            contractDate: data.contractDate || undefined,
            contractItems: data.contractItems || undefined,
            deposit: data.deposit || undefined,
            rebateRate: data.rebateRate || undefined,
            rebateAmount: data.rebateAmount || undefined,
            expectedPaymentDate: data.expectedPaymentDate || undefined,
          })
        });
        if (response.ok) {
          alert('상품이 등록되었습니다.');
          setShowAddProductModal(false);
          fetchProducts();
        } else {
          const error = await response.json();
          alert(`등록 실패: ${error.message}`);
        }
      }
    } catch (error) {
      console.error('Save Product Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleDeleteProduct = async (itemIdx: number) => {
    if (!window.confirm('정말 이 상품을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/product/${itemIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('삭제되었습니다.');
        fetchProducts();
      } else {
        const err = await response.json();
        alert(`삭제 실패: ${err.message}`);
      }
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  };

  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setShowAddProductModal(true);
  };

  const formatVendorCategory = (product: Product) => {
    const parent = product.vendorParentCategory;
    const child = product.vendorCategory;
    if (parent && parent !== '-' && child && child !== '-' && parent !== child) {
      return `${parent} > ${child}`;
    }
    return child || parent || '-';
  };

  // vendorCategory(소분류)에서 대분류 찾기
  const getParentCategory = (product: Product): string => {
    // 1) vendorParentCategory가 있으면 사용
    if (product.vendorParentCategory && product.vendorParentCategory !== '-') {
      return product.vendorParentCategory;
    }
    // 2) categoryNameMap으로 소분류 → 대분류 매핑
    const vc = product.vendorCategory;
    if (vc && vc !== '-') {
      const parent = categoryNameMap.get(vc);
      if (parent) return parent;
    }
    return '미지정';
  };

  // 3개 테이블로 분류: 일반 / 혼수업체 / 리베이트(웨딩홀+허니문)
  const { generalProducts, honsuProducts, rebateProducts } = useMemo(() => {
    const general: Product[] = [];
    const honsu: Product[] = [];
    const rebate: Product[] = [];

    for (const product of products) {
      const parentCat = getParentCategory(product);
      if (parentCat === '혼수업체') {
        honsu.push(product);
      } else if (parentCat === '웨딩홀' || parentCat === '허니문') {
        rebate.push(product);
      } else {
        general.push(product);
      }
    }
    return { generalProducts: general, honsuProducts: honsu, rebateProducts: rebate };
  }, [products, categoryNameMap]);

  const actionButtons = (product: Product) => (
    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
      <button onClick={() => handleEditClick(product)} className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer">수정</button>
      <button onClick={() => handleDeleteProduct(product.customerProductItemIdx)} className="text-red-600 hover:text-red-900 cursor-pointer">삭제</button>
    </td>
  );

  const sectionHeader = (icon: string, title: string, count: number, color: string) => (
    <div className="p-5 border-b border-gray-200 flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <i className={`${icon} text-base`}></i>
      </div>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{count}건</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">계약 목록</h3>
        <button onClick={() => { setEditingProduct(null); setShowAddProductModal(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer flex items-center">
          <i className="ri-add-line mr-2"></i> 계약 추가
        </button>
      </div>

      {/* 1. 일반 계약 테이블 */}
      {generalProducts.length > 0 && (() => {
        const totalPrice = generalProducts.reduce((s, p) => s + p.salesPrice, 0);
        const totalSum = generalProducts.reduce((s, p) => s + p.salesPrice * p.quantity, 0);
        const totalCost = generalProducts.reduce((s, p) => s + p.costPrice, 0);
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {sectionHeader('ri-shopping-bag-line', '일반 계약', generalProducts.length, 'bg-blue-100 text-blue-600')}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체 카테고리</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체명</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">진행구분</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">상품명</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">수량</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">단가</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">합계</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">메모</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {generalProducts.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleEditClick(p)}>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatVendorCategory(p)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.vendor || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.category || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.productName}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.quantity}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.salesPrice.toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{(p.salesPrice * p.quantity).toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate" title={p.memo || ''}>{p.memo || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleEditClick(p)} className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer">수정</button>
                        <button onClick={() => handleDeleteProduct(p.customerProductItemIdx)} className="text-red-600 hover:text-red-900 cursor-pointer">삭제</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={5} className="px-6 py-3 text-sm text-gray-700 text-right">합계</td>
                    <td className="px-6 py-3 text-sm text-gray-900">{totalPrice.toLocaleString()}원</td>
                    <td className="px-6 py-3 text-sm text-gray-900">{totalSum.toLocaleString()}원</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* 2. 혼수업체 테이블 */}
      {honsuProducts.length > 0 && (() => {
        const totalDeposit = honsuProducts.reduce((s, p) => s + ((p as any).deposit || 0), 0);
        const totalRebate = honsuProducts.reduce((s, p) => s + ((p as any).rebateAmount || 0), 0);
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {sectionHeader('ri-gift-line', '혼수업체', honsuProducts.length, 'bg-purple-100 text-purple-600')}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체 카테고리</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체명</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약품목</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약일자</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약금</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">리베이트%</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">리베이트 금액</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">입금예정일</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {honsuProducts.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleEditClick(p)}>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatVendorCategory(p)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.vendor || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.productName}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).contractDate || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{((p as any).deposit || 0).toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).rebateRate || '-'}%</td>
                      <td className="px-6 py-4 text-sm font-medium text-purple-600">{((p as any).rebateAmount || 0).toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).expectedPaymentDate || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleEditClick(p)} className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer">수정</button>
                        <button onClick={() => handleDeleteProduct(p.customerProductItemIdx)} className="text-red-600 hover:text-red-900 cursor-pointer">삭제</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50 font-semibold">
                    <td colSpan={4} className="px-6 py-3 text-sm text-purple-700 text-right">합계</td>
                    <td className="px-6 py-3 text-sm text-purple-700">{totalDeposit.toLocaleString()}원</td>
                    <td></td>
                    <td className="px-6 py-3 text-sm text-purple-700">{totalRebate.toLocaleString()}원</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* 3. 웨딩홀/허니문 리베이트 테이블 */}
      {rebateProducts.length > 0 && (() => {
        const totalDeposit = rebateProducts.reduce((s, p) => s + ((p as any).deposit || 0), 0);
        const totalRebate = rebateProducts.reduce((s, p) => s + ((p as any).rebateAmount || 0), 0);
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {sectionHeader('ri-building-4-line', '웨딩홀 / 허니문', rebateProducts.length, 'bg-amber-100 text-amber-600')}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체 카테고리</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">업체명</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약품목</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약일자</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">계약금</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">리베이트%</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">리베이트 금액</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">입금예정일</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rebateProducts.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleEditClick(p)}>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatVendorCategory(p)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{p.vendor || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.productName}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).contractDate || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{((p as any).deposit || 0).toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).rebateRate || '-'}%</td>
                      <td className="px-6 py-4 text-sm font-medium text-amber-600">{((p as any).rebateAmount || 0).toLocaleString()}원</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{(p as any).expectedPaymentDate || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleEditClick(p)} className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer">수정</button>
                        <button onClick={() => handleDeleteProduct(p.customerProductItemIdx)} className="text-red-600 hover:text-red-900 cursor-pointer">삭제</button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50 font-semibold">
                    <td colSpan={4} className="px-6 py-3 text-sm text-amber-700 text-right">합계</td>
                    <td className="px-6 py-3 text-sm text-amber-700">{totalDeposit.toLocaleString()}원</td>
                    <td></td>
                    <td className="px-6 py-3 text-sm text-amber-700">{totalRebate.toLocaleString()}원</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* 데이터 없을 때 */}
      {products.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-400">
          <i className="ri-file-list-3-line text-4xl mb-2 block"></i>
          등록된 계약이 없습니다.
        </div>
      )}
      
      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => { setShowAddProductModal(false); setEditingProduct(null); }}
        onSave={handleSaveCustomerProduct}
        initialData={editingProduct ? {
          productIdx: editingProduct.productIdx,
          vendorName: editingProduct.vendor,
          productName: editingProduct.productName,
          categoryName: editingProduct.category,
          quantity: editingProduct.quantity,
          supplyPrice: editingProduct.costPrice,
          salesPrice: editingProduct.salesPrice,
          vat: editingProduct.vat,
          memo: editingProduct.memo,
          contractDate: (editingProduct as any).contractDate,
          contractItems: (editingProduct as any).contractItems,
          deposit: (editingProduct as any).deposit,
          rebateRate: (editingProduct as any).rebateRate,
          rebateAmount: (editingProduct as any).rebateAmount,
          expectedPaymentDate: (editingProduct as any).expectedPaymentDate,
          vendorIdx: 0, partnerVendorIdx: 0, vendorCategoryIdx: 0
        } : null}
      />
    </div>
  );
}


// import type { Product } from '../../types';

// interface Props {
//   products: Product[];
//   onAddProductClick: () => void;
//   // ✅ [추가] 부모로부터 받을 핸들러 정의
//   onEditClick: (product: Product) => void;
//   onDeleteClick: (productIdx: number) => void;
// }

// export default function ProductsTab({ products, onAddProductClick, onEditClick, onDeleteClick }: Props) {
//   return (
//     <div className="space-y-6">
//       <div className="bg-white rounded-lg shadow-sm border border-gray-200">
//         <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//           <h3 className="text-lg font-semibold text-gray-900">상품 목록</h3>
//           <button 
//             onClick={onAddProductClick}
//             className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
//           >
//             <i className="ri-add-line mr-2"></i>
//             상품 추가
//           </button>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="w-full">
//             <thead className="bg-gray-50 border-b border-gray-200">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">구분</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상품명</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">카테고리</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">수량</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">단가</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">합계</th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
//                 <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {products.map((product) => (
//                 <tr key={product.id} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
//                       {product.vendor === '-' ? '직접입력' : '제휴업체'}
//                     </span>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{product.productName}</div></td>
//                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-500">{product.category}</div></td>
//                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{product.quantity}</div></td>
//                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-900">{product.salesPrice.toLocaleString()}원</div></td>
//                   <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm font-medium text-gray-900">{(product.salesPrice * product.quantity).toLocaleString()}원</div></td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <span className={`px-2 py-1 text-xs font-medium rounded-full ${product.orderStatus === '확정' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
//                       {product.orderStatus}
//                     </span>
//                   </td>
                  
//                   {/* ✅ [수정] 관리 버튼 영역 */}
//                   <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                     <button 
//                       onClick={() => onEditClick(product)}
//                       className="text-teal-600 hover:text-teal-900 mr-3 cursor-pointer whitespace-nowrap"
//                     >
//                       수정
//                     </button>
//                     <button 
//                       onClick={() => onDeleteClick(product.customerProductItemIdx)}
//                       className="text-red-600 hover:text-red-900 cursor-pointer whitespace-nowrap"
//                     >
//                       삭제
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// }
